export interface WorkflowAdmissionPolicyRecord {
  source?: string;
  codebaseId?: string;
  codebaseName?: string;
  templateId?: string;
  tenantId?: string;
  plan?: string;
  maxActiveGlobal?: number;
  maxActivePerCodebase?: number;
  limits?: {
    maxActiveGlobal?: number;
    maxActivePerCodebase?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface WorkflowAdmissionPolicyTemplate {
  id: string;
  name: string;
  description?: string;
  tags?: string[];
  owner?: string;
  rolloutState?: 'draft' | 'active' | 'archived';
  allowCodebaseOverride?: boolean;
  lockedFields?: string[];
  policy: WorkflowAdmissionPolicyRecord;
}

export interface WorkflowAdmissionPolicyDraft {
  templateId: string;
  source: string;
  tenantId: string;
  plan: string;
  maxActiveGlobal: string;
  maxActivePerCodebase: string;
}

export interface WorkflowAdmissionPolicySummaryItem {
  label: string;
  value: string;
}

export interface WorkflowAdmissionPolicySummary {
  limits: WorkflowAdmissionPolicySummaryItem[];
  metadata: WorkflowAdmissionPolicySummaryItem[];
  lockedFields: WorkflowAdmissionPolicySummaryItem[];
  customFieldCount: number;
  hasPolicy: boolean;
}

export interface WorkflowAdmissionPolicyResolution {
  policy: WorkflowAdmissionPolicyRecord | null;
  template: WorkflowAdmissionPolicyTemplate | null;
  lockedFields: string[];
}

const MANAGED_FIELD_KEYS = [
  'source',
  'tenantId',
  'plan',
  'maxActiveGlobal',
  'maxActivePerCodebase',
] as const;

type ManagedFieldKey = (typeof MANAGED_FIELD_KEYS)[number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toIntegerString(value: unknown): string {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
    return String(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    const parsed = Number(trimmed);
    if (Number.isInteger(parsed) && parsed >= 0) {
      return String(parsed);
    }
  }
  return '';
}

function parseNonNegativeInteger(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0) return undefined;
  return parsed;
}

function readLimit(
  policy: WorkflowAdmissionPolicyRecord | null | undefined,
  key: 'maxActiveGlobal' | 'maxActivePerCodebase'
): string {
  if (!policy) return '';
  return toIntegerString(policy[key] ?? policy.limits?.[key]);
}

function mergeLimits(
  base: WorkflowAdmissionPolicyRecord | undefined,
  override: WorkflowAdmissionPolicyRecord | undefined
): WorkflowAdmissionPolicyRecord['limits'] | undefined {
  const merged = {
    ...(isRecord(base?.limits) ? base?.limits : {}),
    ...(isRecord(override?.limits) ? override?.limits : {}),
  };
  return Object.keys(merged).length > 0 ? merged : undefined;
}

function normalizeLockedFields(fields: string[] | undefined): string[] {
  return [...new Set((fields ?? []).map(field => field.trim()).filter(Boolean))];
}

function findTemplate(
  templates: WorkflowAdmissionPolicyTemplate[] | undefined,
  templateId: string
): WorkflowAdmissionPolicyTemplate | null {
  return templates?.find(template => template.id === templateId) ?? null;
}

function isFieldLocked(lockedFields: string[], key: ManagedFieldKey): boolean {
  return lockedFields.includes(key) || lockedFields.includes(`limits.${key}`);
}

function getManagedFieldValue(
  policy: WorkflowAdmissionPolicyRecord | null | undefined,
  key: ManagedFieldKey
): unknown {
  if (!policy) return undefined;
  if (key === 'maxActiveGlobal' || key === 'maxActivePerCodebase') {
    return policy[key] ?? policy.limits?.[key];
  }
  return policy[key];
}

function setManagedFieldValue(
  policy: WorkflowAdmissionPolicyRecord,
  key: ManagedFieldKey,
  value: unknown
): void {
  if (key === 'maxActiveGlobal' || key === 'maxActivePerCodebase') {
    const limits = isRecord(policy.limits) ? { ...policy.limits } : {};
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
      policy[key] = value;
      limits[key] = value;
      policy.limits = limits;
      return;
    }
    Reflect.deleteProperty(policy, key);
    Reflect.deleteProperty(limits, key);
    if (Object.keys(limits).length > 0) {
      policy.limits = limits;
    } else {
      delete policy.limits;
    }
    return;
  }

  if (typeof value === 'string' && value.trim()) {
    policy[key] = value.trim();
  } else {
    Reflect.deleteProperty(policy, key);
  }
}

function removeManagedFieldValue(
  policy: WorkflowAdmissionPolicyRecord,
  key: ManagedFieldKey
): void {
  if (key === 'maxActiveGlobal' || key === 'maxActivePerCodebase') {
    Reflect.deleteProperty(policy, key);
    if (isRecord(policy.limits)) {
      const limits = { ...policy.limits };
      Reflect.deleteProperty(limits, key);
      if (Object.keys(limits).length > 0) {
        policy.limits = limits;
      } else {
        delete policy.limits;
      }
    }
    return;
  }

  Reflect.deleteProperty(policy, key);
}

function valuesEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function mergeWorkflowAdmissionPolicies(
  base: WorkflowAdmissionPolicyRecord,
  override: WorkflowAdmissionPolicyRecord
): WorkflowAdmissionPolicyRecord {
  const merged: WorkflowAdmissionPolicyRecord = { ...base, ...override };
  const limits = mergeLimits(base, override);
  if (limits) {
    merged.limits = limits;
  } else {
    delete merged.limits;
  }
  return merged;
}

function syncWorkflowAdmissionManagedFields(policy: WorkflowAdmissionPolicyRecord): void {
  const limits = isRecord(policy.limits) ? { ...policy.limits } : {};

  for (const key of ['maxActiveGlobal', 'maxActivePerCodebase'] as const) {
    const topLevel = policy[key];
    const nested = limits[key];

    if (typeof topLevel === 'number' && Number.isFinite(topLevel) && topLevel >= 0) {
      limits[key] = topLevel;
      continue;
    }

    if (typeof nested === 'number' && Number.isFinite(nested) && nested >= 0) {
      policy[key] = nested;
      limits[key] = nested;
      continue;
    }

    Reflect.deleteProperty(policy, key);
    Reflect.deleteProperty(limits, key);
  }

  if (Object.keys(limits).length > 0) {
    policy.limits = limits;
  } else {
    delete policy.limits;
  }
}

function resolveWorkflowAdmissionPolicyTemplate(
  policy: WorkflowAdmissionPolicyRecord | null | undefined,
  templates: WorkflowAdmissionPolicyTemplate[] | undefined
): WorkflowAdmissionPolicyResolution {
  if (!isRecord(policy)) {
    return { policy: null, template: null, lockedFields: [] };
  }

  const rawPolicy: WorkflowAdmissionPolicyRecord = { ...policy };
  const templateId = toTrimmedString(rawPolicy.templateId);
  if (!templateId) {
    return { policy: rawPolicy, template: null, lockedFields: [] };
  }

  const template = findTemplate(templates, templateId);
  if (!template) {
    return { policy: rawPolicy, template: null, lockedFields: [] };
  }

  const lockedFields = normalizeLockedFields(template.lockedFields);
  const effective = mergeWorkflowAdmissionPolicies(template.policy, rawPolicy);
  effective.templateId = template.id;

  for (const key of MANAGED_FIELD_KEYS) {
    if (!isFieldLocked(lockedFields, key)) continue;
    const templateValue = getManagedFieldValue(template.policy, key);
    if (templateValue === undefined) {
      removeManagedFieldValue(effective, key);
    } else {
      setManagedFieldValue(effective, key, templateValue);
    }
  }

  syncWorkflowAdmissionManagedFields(effective);

  return {
    policy: effective,
    template,
    lockedFields,
  };
}

function canonicalizeWorkflowAdmissionPolicy(
  policy: WorkflowAdmissionPolicyRecord | null | undefined,
  templates: WorkflowAdmissionPolicyTemplate[] | undefined
): WorkflowAdmissionPolicyRecord | null {
  const resolved = resolveWorkflowAdmissionPolicyTemplate(policy, templates);
  if (!resolved.policy) return null;
  if (!resolved.template) {
    return Object.keys(resolved.policy).length === 0 ? null : resolved.policy;
  }

  const canonical: WorkflowAdmissionPolicyRecord = { ...resolved.policy };
  const template = resolved.template;

  for (const key of MANAGED_FIELD_KEYS) {
    const templateValue = getManagedFieldValue(template.policy, key);
    const currentValue = getManagedFieldValue(canonical, key);

    if (isFieldLocked(resolved.lockedFields, key)) {
      removeManagedFieldValue(canonical, key);
      continue;
    }

    if (templateValue !== undefined && valuesEqual(currentValue, templateValue)) {
      removeManagedFieldValue(canonical, key);
    }
  }

  if (Object.keys(canonical.limits ?? {}).length === 0) {
    delete canonical.limits;
  }

  syncWorkflowAdmissionManagedFields(canonical);

  return Object.keys(canonical).length === 0 ? null : canonical;
}

export function createWorkflowAdmissionPolicyDraft(
  policy: WorkflowAdmissionPolicyRecord | null | undefined,
  templates: WorkflowAdmissionPolicyTemplate[] = []
): WorkflowAdmissionPolicyDraft {
  const resolved = resolveWorkflowAdmissionPolicyTemplate(policy, templates);
  const effective = resolved.policy ?? {};
  const templateId = resolved.template?.id ?? toTrimmedString(effective.templateId);

  return {
    templateId,
    source: toTrimmedString(effective.source),
    tenantId: toTrimmedString(effective.tenantId),
    plan: toTrimmedString(effective.plan),
    maxActiveGlobal: readLimit(effective, 'maxActiveGlobal'),
    maxActivePerCodebase: readLimit(effective, 'maxActivePerCodebase'),
  };
}

export function formatWorkflowAdmissionPolicyJson(
  policy: WorkflowAdmissionPolicyRecord | null | undefined
): string {
  return JSON.stringify(policy ?? {}, null, 2);
}

export function parseWorkflowAdmissionPolicyJson(value: string): {
  policy: WorkflowAdmissionPolicyRecord | null;
  error?: string;
} {
  const trimmed = value.trim();
  if (!trimmed) return { policy: null };

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!isRecord(parsed)) {
      return { policy: null, error: 'Policy JSON must be an object.' };
    }
    return Object.keys(parsed).length === 0 ? { policy: null } : { policy: parsed };
  } catch (error) {
    return {
      policy: null,
      error: error instanceof Error ? error.message : 'Invalid JSON',
    };
  }
}

export function normalizeWorkflowAdmissionPolicyRecord(
  policy: WorkflowAdmissionPolicyRecord | null | undefined,
  templates: WorkflowAdmissionPolicyTemplate[] = []
): WorkflowAdmissionPolicyRecord | null {
  return canonicalizeWorkflowAdmissionPolicy(policy, templates);
}

export function buildWorkflowAdmissionPolicyFromDraft(
  basePolicy: WorkflowAdmissionPolicyRecord | null | undefined,
  draft: WorkflowAdmissionPolicyDraft,
  templates: WorkflowAdmissionPolicyTemplate[] = []
): WorkflowAdmissionPolicyRecord | null {
  const next: WorkflowAdmissionPolicyRecord = isRecord(basePolicy) ? { ...basePolicy } : {};
  const templateId = draft.templateId.trim();
  const template = templateId ? findTemplate(templates, templateId) : null;
  const lockedFields = normalizeLockedFields(template?.lockedFields);

  if (templateId) {
    next.templateId = templateId;
  } else {
    delete next.templateId;
  }

  const maxActiveGlobal = parseNonNegativeInteger(draft.maxActiveGlobal);
  const maxActivePerCodebase = parseNonNegativeInteger(draft.maxActivePerCodebase);

  if (isFieldLocked(lockedFields, 'maxActiveGlobal')) {
    removeManagedFieldValue(next, 'maxActiveGlobal');
  } else if (maxActiveGlobal === undefined) {
    removeManagedFieldValue(next, 'maxActiveGlobal');
  } else {
    setManagedFieldValue(next, 'maxActiveGlobal', maxActiveGlobal);
  }

  if (isFieldLocked(lockedFields, 'maxActivePerCodebase')) {
    removeManagedFieldValue(next, 'maxActivePerCodebase');
  } else if (maxActivePerCodebase === undefined) {
    removeManagedFieldValue(next, 'maxActivePerCodebase');
  } else {
    setManagedFieldValue(next, 'maxActivePerCodebase', maxActivePerCodebase);
  }

  if (isFieldLocked(lockedFields, 'source')) {
    removeManagedFieldValue(next, 'source');
  } else if (draft.source.trim()) {
    setManagedFieldValue(next, 'source', draft.source);
  } else {
    removeManagedFieldValue(next, 'source');
  }

  if (isFieldLocked(lockedFields, 'tenantId')) {
    removeManagedFieldValue(next, 'tenantId');
  } else if (draft.tenantId.trim()) {
    setManagedFieldValue(next, 'tenantId', draft.tenantId);
  } else {
    removeManagedFieldValue(next, 'tenantId');
  }

  if (isFieldLocked(lockedFields, 'plan')) {
    removeManagedFieldValue(next, 'plan');
  } else if (draft.plan.trim()) {
    setManagedFieldValue(next, 'plan', draft.plan);
  } else {
    removeManagedFieldValue(next, 'plan');
  }

  return canonicalizeWorkflowAdmissionPolicy(next, templates);
}

export function summarizeWorkflowAdmissionPolicy(
  policy: WorkflowAdmissionPolicyRecord | null | undefined,
  templates: WorkflowAdmissionPolicyTemplate[] = []
): WorkflowAdmissionPolicySummary {
  if (!policy) {
    return {
      limits: [],
      metadata: [],
      lockedFields: [],
      customFieldCount: 0,
      hasPolicy: false,
    };
  }

  const resolved = resolveWorkflowAdmissionPolicyTemplate(policy, templates);
  const effective = resolved.policy ?? policy;

  const limits = [
    effective.maxActiveGlobal ?? effective.limits?.maxActiveGlobal,
    effective.maxActivePerCodebase ?? effective.limits?.maxActivePerCodebase,
  ];

  const limitItems: WorkflowAdmissionPolicySummaryItem[] = [];
  if (limits[0] !== undefined) {
    limitItems.push({ label: 'Global active runs', value: String(limits[0]) });
  }
  if (limits[1] !== undefined) {
    limitItems.push({ label: 'Per-project active runs', value: String(limits[1]) });
  }

  const metadataItems: WorkflowAdmissionPolicySummaryItem[] = [];
  if (effective.source) metadataItems.push({ label: 'Source', value: effective.source });
  if (effective.plan) metadataItems.push({ label: 'Plan', value: effective.plan });
  if (effective.tenantId) metadataItems.push({ label: 'Tenant', value: effective.tenantId });
  if (resolved.template) {
    metadataItems.push({ label: 'Template', value: resolved.template.name });
  } else if (policy.templateId) {
    metadataItems.push({ label: 'Template', value: policy.templateId });
  }

  const lockedFieldItems: WorkflowAdmissionPolicySummaryItem[] = resolved.lockedFields.map(
    field => ({
      label: 'Locked',
      value: field,
    })
  );

  const knownKeys = new Set([
    'source',
    'codebaseId',
    'codebaseName',
    'templateId',
    'tenantId',
    'plan',
    'maxActiveGlobal',
    'maxActivePerCodebase',
    'limits',
  ]);
  const customFieldCount = Object.keys(policy).filter(key => !knownKeys.has(key)).length;

  return {
    limits: limitItems,
    metadata: metadataItems,
    lockedFields: lockedFieldItems,
    customFieldCount,
    hasPolicy:
      limitItems.length > 0 ||
      metadataItems.length > 0 ||
      lockedFieldItems.length > 0 ||
      customFieldCount > 0,
  };
}
